'use client';

import { Building2, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import GlassPanel from '@/components/dashboard/GlassPanel';
import { SkeletonGrid } from '@/components/dashboard/LoadingState';
import { Meridian } from '@/components/brand/Meridian';
import type { PublicInstitute } from '@/lib/api/institute';

export interface InstituteListProps {
  institutes: PublicInstitute[];
  isLoading: boolean;
  error: Error | null;
  onRefetch?: () => void;
  count: number;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
}

export function InstituteList({
  institutes,
  isLoading,
  error,
  onRefetch,
  count,
  page,
  totalPages,
  onPageChange,
  className,
}: InstituteListProps) {
  return (
    <div className={cn('space-y-6', className)}>
      <p className="text-sm text-muted">
        Showing <span className="font-medium text-foreground">{count}</span> institutes
      </p>

      {isLoading && (
        <SkeletonGrid count={4} className="grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" itemClassName="h-40" />
      )}

      {!isLoading && error && (
        <div className="flex flex-col items-center justify-center py-20">
          <AlertCircle className="h-8 w-8 text-red-400" />
          <p className="mt-2 text-sm text-red-600">
            {error?.message ?? 'Failed to load institutes'}
          </p>
          {onRefetch && (
            <button
              onClick={onRefetch}
              className="mt-4 rounded-lg bg-primary px-4 py-2 text-sm text-white"
            >
              Retry
            </button>
          )}
        </div>
      )}

      {!isLoading && !error && institutes.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {institutes.map((institute) => (
            <InstituteCard key={institute.id} institute={institute} />
          ))}
        </div>
      )}

      {!isLoading && !error && institutes.length === 0 && (
        <GlassPanel className="p-12 text-center">
          <Meridian state="empty" width={140} />
          <Building2 className="mx-auto h-12 w-12 text-muted" />
          <h3 className="mt-4 text-lg font-semibold text-foreground">No institutes found</h3>
          <p className="mt-1 text-sm text-ash">
            Try adjusting your filters or search for a different location.
          </p>
        </GlassPanel>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            className={cn(
              'rounded-lg border px-4 py-2 text-sm font-medium transition-all',
              page <= 1
                ? 'border-neon-cyan/5 bg-card text-muted'
                : 'border-neon-cyan/10 bg-card text-ash hover:bg-panel'
            )}
          >
            Previous
          </button>
          <span className="text-sm text-ash">
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
            className={cn(
              'rounded-lg border px-4 py-2 text-sm font-medium transition-all',
              page >= totalPages
                ? 'border-neon-cyan/5 bg-card text-muted'
                : 'border-neon-cyan/10 bg-card text-ash hover:bg-panel'
            )}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

function InstituteCard({ institute }: { institute: PublicInstitute }) {
  const location = [institute.cityCountry, institute.countryCountry]
    .filter(Boolean)
    .join(', ');

  return (
    <GlassPanel className="flex flex-col p-5 transition hover:border-neon-cyan/20">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <h3 className="text-base font-semibold text-foreground">{institute.instituteName}</h3>
          {location && (
            <p className="mt-1 text-sm text-ash">{location}</p>
          )}
        </div>
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-neon-cyan/10 text-neon-cyan">
          <Building2 className="h-5 w-5" />
        </div>
      </div>

      {institute.courseOfferingCount > 0 && (
        <div className="mt-4 pt-4 border-t border-border/50">
          <p className="text-sm text-ash">
            <span className="font-medium text-foreground">{institute.courseOfferingCount}</span>{' '}
            published course{institute.courseOfferingCount === 1 ? '' : 's'}
          </p>
        </div>
      )}
    </GlassPanel>
  );
}
